import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import './AdminContractorApplications.css';

const AdminContractorApplications = () => {
  const [applications, setApplications] = useState([]);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [adminNotes, setAdminNotes] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectionModal, setShowRejectionModal] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    fetchApplications();
  }, [statusFilter, currentPage]);

  const fetchApplications = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/contractor-applications/list', {
        params: {
          status: statusFilter,
          page: currentPage,
          limit: 20
        },
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      setApplications(response.data.applications);
      setTotalCount(response.data.total);
    } catch (error) {
      console.error('신청 목록 조회 오류:', error);
      if (error.response?.status === 403) {
        alert('권한이 없습니다.');
        navigate('/');
      }
    } finally {
      setLoading(false);
    }
  };

  const viewDetail = async (id) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/contractor-applications/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      setSelectedApplication(response.data);
      setAdminNotes(response.data.admin_notes || '');
      setShowModal(true);
    } catch (error) {
      console.error('상세 조회 오류:', error);
      alert('상세 정보를 불러올 수 없습니다.');
    }
  };

  const approveApplication = async () => {
    if (!selectedApplication) return;
    
    if (!window.confirm('정말 승인하시겠습니까? 소장 계정이 생성됩니다.')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `/api/contractor-applications/${selectedApplication.id}/approve`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      alert('소장 계정이 생성되었습니다.');
      setShowModal(false);
      fetchApplications();
    } catch (error) {
      console.error('승인 오류:', error);
      alert(error.response?.data?.error || '승인 처리 중 오류가 발생했습니다.');
    }
  };

  const rejectApplication = async () => {
    if (!selectedApplication || !rejectionReason.trim()) {
      alert('거절 사유를 입력해주세요.');
      return;
    }
    
    if (!window.confirm('정말 거절하시겠습니까?')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `/api/contractor-applications/${selectedApplication.id}/reject`,
        { rejection_reason: rejectionReason },
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      alert('신청이 거절되었습니다.');
      setShowRejectionModal(false);
      setShowModal(false);
      setRejectionReason('');
      fetchApplications();
    } catch (error) {
      console.error('거절 오류:', error);
      alert('거절 처리 중 오류가 발생했습니다.');
    }
  };

  const deleteApplication = async (id) => {
    if (!window.confirm('정말 이 신청을 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `/api/contractor-applications/${id}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      alert('신청이 삭제되었습니다.');
      setShowModal(false);
      fetchApplications();
    } catch (error) {
      console.error('삭제 오류:', error);
      alert(error.response?.data?.error || '삭제 처리 중 오류가 발생했습니다.');
    }
  };

  const updateNotes = async () => {
    if (!selectedApplication) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `/api/contractor-applications/${selectedApplication.id}/notes`,
        { notes: adminNotes },
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      alert('메모가 저장되었습니다.');
    } catch (error) {
      console.error('메모 저장 오류:', error);
      alert('메모 저장 중 오류가 발생했습니다.');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { text: '대기', className: 'badge-pending' },
      reviewed: { text: '검토', className: 'badge-reviewed' },
      approved: { text: '승인', className: 'badge-approved' },
      rejected: { text: '거절', className: 'badge-rejected' }
    };
    return badges[status] || badges.pending;
  };

  return (
    <div className="admin-contractor-applications">
      <div className="applications-header">
        <h1>시공점 입점 신청 관리</h1>
        <div className="header-stats">
          <span className="stat-item">전체: {totalCount}건</span>
        </div>
      </div>

      <div className="filter-tabs">
        <button 
          className={statusFilter === 'pending' ? 'active' : ''}
          onClick={() => {
            setStatusFilter('pending');
            setCurrentPage(1);
          }}
        >
          대기중
        </button>
        <button 
          className={statusFilter === 'all' ? 'active' : ''}
          onClick={() => {
            setStatusFilter('all');
            setCurrentPage(1);
          }}
        >
          전체
        </button>
        <button 
          className={statusFilter === 'approved' ? 'active' : ''}
          onClick={() => {
            setStatusFilter('approved');
            setCurrentPage(1);
          }}
        >
          승인
        </button>
        <button 
          className={statusFilter === 'rejected' ? 'active' : ''}
          onClick={() => {
            setStatusFilter('rejected');
            setCurrentPage(1);
          }}
        >
          거절
        </button>
      </div>

      {loading ? (
        <div className="loading">데이터를 불러오는 중...</div>
      ) : (
        <div className="applications-table">
          <table>
            <thead>
              <tr>
                <th>신청일</th>
                <th>업체명</th>
                <th>대표자</th>
                <th>사업자번호</th>
                <th>연락처</th>
                <th>전문분야</th>
                <th>상태</th>
                <th>작업</th>
              </tr>
            </thead>
            <tbody>
              {applications.length === 0 ? (
                <tr>
                  <td colSpan="8" className="no-data">신청 내역이 없습니다.</td>
                </tr>
              ) : (
                applications.map(app => (
                  <tr key={app.id}>
                    <td>{formatDate(app.created_at)}</td>
                    <td className="company-name">{app.company_name}</td>
                    <td>{app.representative_name}</td>
                    <td>{app.business_number}</td>
                    <td>{app.phone}</td>
                    <td className="specialties">
                      {Array.isArray(app.specialties) 
                        ? app.specialties.slice(0, 2).join(', ') + (app.specialties.length > 2 ? ` 외 ${app.specialties.length - 2}개` : '')
                        : typeof app.specialties === 'string' 
                          ? (() => {
                              try {
                                const parsed = JSON.parse(app.specialties);
                                return Array.isArray(parsed) 
                                  ? parsed.slice(0, 2).join(', ') + (parsed.length > 2 ? ` 외 ${parsed.length - 2}개` : '')
                                  : '데이터 오류';
                              } catch {
                                return app.specialties;
                              }
                            })()
                          : '없음'}
                    </td>
                    <td>
                      <span className={`badge ${getStatusBadge(app.status).className}`}>
                        {getStatusBadge(app.status).text}
                      </span>
                    </td>
                    <td>
                      <button 
                        className="btn-detail"
                        onClick={() => viewDetail(app.id)}
                      >
                        상세보기
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* 상세 모달 */}
      {showModal && selectedApplication && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>입점 신청 상세</h2>
              <button className="btn-close" onClick={() => setShowModal(false)}>×</button>
            </div>
            
            <div className="modal-body">
              <div className="detail-section">
                <h3>기본 정보</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>업체명</label>
                    <span>{selectedApplication.company_name}</span>
                  </div>
                  <div className="detail-item">
                    <label>대표자명</label>
                    <span>{selectedApplication.representative_name}</span>
                  </div>
                  <div className="detail-item">
                    <label>사업자등록번호</label>
                    <span>{selectedApplication.business_number}</span>
                  </div>
                  <div className="detail-item">
                    <label>주민등록번호</label>
                    <span>{selectedApplication.resident_number}</span>
                  </div>
                  <div className="detail-item">
                    <label>주소</label>
                    <span>{selectedApplication.address}</span>
                  </div>
                </div>
              </div>

              <div className="detail-section">
                <h3>계정 정보</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>로그인 ID</label>
                    <span>{selectedApplication.login_id}</span>
                  </div>
                  <div className="detail-item">
                    <label>비밀번호</label>
                    <span>{selectedApplication.password}</span>
                  </div>
                  <div className="detail-item">
                    <label>이메일</label>
                    <span>{selectedApplication.email}</span>
                  </div>
                  <div className="detail-item">
                    <label>연락처</label>
                    <span>{selectedApplication.phone}</span>
                  </div>
                </div>
              </div>

              <div className="detail-section">
                <h3>전문 분야</h3>
                <div className="specialty-list">
                  {Array.isArray(selectedApplication.specialties) 
                    ? selectedApplication.specialties.map((specialty, index) => (
                        <span key={index} className="specialty-tag">{specialty}</span>
                      ))
                    : typeof selectedApplication.specialties === 'string'
                      ? (() => {
                          try {
                            const parsed = JSON.parse(selectedApplication.specialties);
                            return Array.isArray(parsed) 
                              ? parsed.map((specialty, index) => (
                                  <span key={index} className="specialty-tag">{specialty}</span>
                                ))
                              : <span>데이터 오류</span>;
                          } catch {
                            return <span>{selectedApplication.specialties}</span>;
                          }
                        })()
                      : <span>없음</span>}
                </div>
              </div>

              {selectedApplication.introduction && (
                <div className="detail-section">
                  <h3>간단 소개</h3>
                  <p className="introduction">{selectedApplication.introduction}</p>
                </div>
              )}

              {selectedApplication.signature_data && (
                <div className="detail-section">
                  <h3>전자 서명</h3>
                  <img 
                    src={selectedApplication.signature_data} 
                    alt="서명" 
                    className="signature-image"
                  />
                </div>
              )}

              {selectedApplication.attachments && Array.isArray(selectedApplication.attachments) && selectedApplication.attachments.length > 0 && (
                <div className="detail-section">
                  <h3>첨부 파일</h3>
                  <div className="attachment-list">
                    {selectedApplication.attachments.map((file, index) => (
                      <a 
                        key={index}
                        href={file.path}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="attachment-item"
                      >
                        {file.originalname}
                      </a>
                    ))}
                  </div>
                </div>
              )}

              <div className="detail-section">
                <h3>관리자 메모</h3>
                <textarea
                  value={adminNotes}
                  onChange={e => setAdminNotes(e.target.value)}
                  placeholder="메모를 입력하세요"
                  rows="4"
                  className="admin-notes"
                />
                <button 
                  className="btn-save-notes"
                  onClick={updateNotes}
                >
                  메모 저장
                </button>
              </div>

              <div className="detail-section">
                <h3>접속 정보</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>신청일시</label>
                    <span>{formatDate(selectedApplication.created_at)}</span>
                  </div>
                  <div className="detail-item">
                    <label>IP 주소</label>
                    <span>{selectedApplication.ip_address}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              {selectedApplication.status === 'pending' && (
                <>
                  <button 
                    className="btn-approve"
                    onClick={approveApplication}
                  >
                    승인
                  </button>
                  <button 
                    className="btn-reject"
                    onClick={() => setShowRejectionModal(true)}
                  >
                    거절
                  </button>
                </>
              )}
              <button 
                className="btn-delete"
                onClick={() => deleteApplication(selectedApplication.id)}
              >
                삭제
              </button>
              <button 
                className="btn-cancel"
                onClick={() => setShowModal(false)}
              >
                닫기
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 거절 사유 모달 */}
      {showRejectionModal && (
        <div className="modal-overlay" onClick={() => setShowRejectionModal(false)}>
          <div className="modal-content small" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>거절 사유 입력</h2>
              <button className="btn-close" onClick={() => setShowRejectionModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <textarea
                value={rejectionReason}
                onChange={e => setRejectionReason(e.target.value)}
                placeholder="거절 사유를 입력하세요"
                rows="5"
                className="rejection-reason"
              />
            </div>
            <div className="modal-footer">
              <button 
                className="btn-reject"
                onClick={rejectApplication}
              >
                거절 처리
              </button>
              <button 
                className="btn-cancel"
                onClick={() => setShowRejectionModal(false)}
              >
                취소
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminContractorApplications;